<template>
  <div class="search-resource-detail-question-parts">
    <selection-bar :count="selectedCount" @clear="clearAllSelections" />
    <div class="question-parts">
      <!-- top parent -->
      <div class="question-wrapper">
        <input-checkbox
          v-if="selectable"
          :id="'SRDQP_' + mpqQuestion.uuid"
          kind="white"
          :disabled="disableGrandParentQuestion"
          value="true"
          :selected-values="[String(isQuestionSelected(mpqQuestion.uuid))]"
          @change="handleQuestionSelectionChange(mpqQuestion)"
        />
        <svg-icon
          v-if="isCurrentQuestion(mpqQuestion.uuid)"
          name="CourseLocator32"
          kind="indicative"
        />
        <svg-icon class="question-icon" name="MPQ32" kind="indicative" />
        <span class="question-body">{{ mpqQuestion.questionBodyText }}</span>
        <cv-button
          kind="ghost"
          size="small"
          class="icon-button"
          @click.prevent="handleChangeQuestionPreview(mpqQuestion)"
        >
          <svg-icon name="Details24" kind="action" />
        </cv-button>
      </div>

      <!-- child questions -->
      <div class="child-questions">
        <template v-for="question in mpqQuestion.children">
          <!-- child question (Layer 2) -->
          <div
            v-if="!isMpqQuestion(question)"
            :key="`${question.id}`"
            class="question-wrapper layer-two"
          >
            <input-checkbox
              v-if="selectable"
              :id="'SRDQP_' + question.uuid"
              kind="white"
              :disabled="false"
              value="true"
              :selected-values="[String(isChildQuestionSelected(question))]"
              @change="handleChildrenQuestionSelectionChange(question, mpqQuestion)"
            />
            <svg-icon
              v-if="isCurrentQuestion(question.uuid)"
              name="CourseLocator32"
              kind="indicative"
            />
            <svg-icon class="question-icon" :name="getIconName(question)" kind="indicative" />
            <span class="question-body">
              <span class="question-label">{{ getChildQuestionLabel(question) }}</span>
              <span class="text">{{ question.questionBodyText }}</span>
            </span>
            <cv-button
              kind="ghost"
              size="small"
              class="icon-button"
              @click.prevent="handleChangeQuestionPreview(question)"
            >
              <svg-icon name="Details24" kind="action" />
            </cv-button>
          </div>

          <AccordionWrapper v-else :key="`accordion-${question.id}`">
            <!-- child mpq question (Layer 2) -->
            <input-checkbox
              v-if="selectable"
              :id="'SRDQP_' + question.uuid"
              kind="white"
              :disabled="addFromMpqSubLayer"
              value="true"
              :selected-values="[String(isChildQuestionSelected(question))]"
              @change="handleChildrenQuestionSelectionChange(question, mpqQuestion)"
            />
            <cv-accordion-item :open="isAccordionOpen(question.uuid)">
              <template #title>
                <svg-icon
                  v-if="isCurrentQuestion(question.uuid)"
                  name="CourseLocator32"
                  kind="indicative"
                />
                <svg-icon class="question-icon" :name="getIconName(question)" kind="indicative" />

                <span class="question-body">
                  <span class="question-label">{{ getChildQuestionLabel(question) }}</span>
                  <span class="text">{{ question.questionBodyText }}</span>
                </span>
              </template>

              <template #content>
                <!-- child question (Layer 3) -->
                <div
                  v-for="childQuestion in question.children"
                  :key="childQuestion.id"
                  class="question-wrapper layer-three"
                >
                  <input-checkbox
                    v-if="selectable"
                    :id="'SRDQP_' + childQuestion.uuid"
                    kind="white"
                    :disabled="false"
                    value="true"
                    :selected-values="[String(isChildQuestionSelected(childQuestion))]"
                    @change="handleChildrenQuestionSelectionChange(childQuestion, question)"
                  />

                  <svg-icon
                    v-if="isCurrentQuestion(childQuestion.uuid)"
                    name="CourseLocator32"
                    kind="indicative"
                  />
                  <svg-icon
                    class="question-icon"
                    :name="getIconName(childQuestion)"
                    kind="indicative"
                  />

                  <span class="question-body">
                    <span class="question-label">{{
                      getGrandChildQuestionLabel(question, childQuestion)
                    }}</span>
                    <span class="text">{{ childQuestion.questionBodyText }}</span>
                  </span>
                  <cv-button
                    kind="ghost"
                    size="small"
                    class="icon-button"
                    @click.prevent="handleChangeQuestionPreview(childQuestion)"
                  >
                    <svg-icon name="Details24" kind="action" />
                  </cv-button>
                </div>
              </template>
            </cv-accordion-item>
            <!-- Button cannot be placed inside cv-accordion-item as clicking on the toggle will 
               also trigger a click event which causes the flickering.
               See cv-accordion-item.vue template for more details.
          -->
            <cv-button
              kind="ghost"
              size="small"
              class="icon-button"
              @click.prevent="handleChangeQuestionPreview(question)"
            >
              <svg-icon name="Details24" kind="action" />
            </cv-button>
          </AccordionWrapper>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, toRefs, onBeforeMount, watch } from "vue";
import { QuestionModel } from "@/modules/editor/types";
import { QuestionType } from "@/types/question";
import { QUESTION_TYPE, ALPHABETS_LIST } from "@modules/editor/service/editor.constant";
import StringUtils from "@utils/string.utils";

interface Props {
  currentQuestion: QuestionModel;
  mpqQuestion: QuestionModel;
  selectable?: boolean;
  addFromMpqChildLayer?: boolean;
  addFromMpqSubLayer?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  selectable: false,
  addFromMpqChildLayer: false,
  addFromMpqSubLayer: false,
});

interface Emits {
  "change-question-preview": [QuestionModel];
  select: [uuids: string[]];
}

const emit = defineEmits<Emits>();

const { mpqQuestion, currentQuestion, selectable, addFromMpqChildLayer, addFromMpqSubLayer } =
  toRefs(props);

// Constants
const QUESTION_ICON_MAP: Record<QuestionType, string> = {
  [QUESTION_TYPE.DND]: "ClickandDrop32",
  [QUESTION_TYPE.EEQ]: "ErrorEditing32",
  [QUESTION_TYPE.FILE]: "RichText32",
  [QUESTION_TYPE.FILL]: "FillInTheBlanks32",
  [QUESTION_TYPE.FREE]: "RichText32",
  [QUESTION_TYPE.MCQ]: "MCQ32",
  [QUESTION_TYPE.MPQ]: "MPQ32",
  [QUESTION_TYPE.MRQ]: "MCQ32",
  [QUESTION_TYPE.REC]: "Audio32",
};

// Data
const firstQuestionViewed = ref<QuestionModel | null>(null);

const selectedQuestionUuids = ref(new Set<string>());

// Computed
const hasSubQuestion = computed(() => {
  return mpqQuestion.value.children.some((question) => question.children.length > 0);
});

const disableGrandParentQuestion = computed(() => {
  return (hasSubQuestion.value && addFromMpqChildLayer.value) || addFromMpqSubLayer.value;
});

const totalQuestionInMpq = computed(() => {
  let totalQuestion = 0;
  mpqQuestion.value.children.forEach((child) => {
    totalQuestion = totalQuestion + 1 + child.children.length;
  });
  return totalQuestion + 1;
});

const selectedCount = computed(() => {
  if (isQuestionSelected(mpqQuestion.value.uuid)) {
    return totalQuestionInMpq.value;
  }
  let totalSelectedCount = 0;
  mpqQuestion.value.children.forEach((child) => {
    if (isChildQuestionSelected(child)) {
      totalSelectedCount = totalSelectedCount + 1 + child.children.length;
    } else {
      child.children.forEach((grandChild) => {
        if (isChildQuestionSelected(grandChild)) {
          totalSelectedCount++;
        }
      });
    }
  });
  return totalSelectedCount;
});

// Methods

const isAccordionOpen = (uuid: string) => {
  return firstQuestionViewed.value?.parentQuestionUuid === uuid;
};

const isMpqQuestion = (question: QuestionModel) => {
  return question.questionType === QUESTION_TYPE.MPQ;
};

const getIconName = (question: QuestionModel) => {
  return QUESTION_ICON_MAP[question.questionType];
};

const isCurrentQuestion = (uuid: string) => {
  return currentQuestion.value?.uuid === uuid;
};

const getChildQuestionLabel = (question: QuestionModel) => {
  return `${ALPHABETS_LIST[question.indexNo].toLowerCase()}.`;
};

const getGrandChildQuestionLabel = (question: QuestionModel, childQuestion: QuestionModel) => {
  return `${getChildQuestionLabel(question)}${StringUtils.getRomanValue(childQuestion.indexNo + 1)}.`;
};

const isQuestionSelected = (uuid: string) => {
  return selectedQuestionUuids.value.has(uuid);
};

const selectQuestion = (uuid: string) => {
  selectedQuestionUuids.value.add(uuid);
};

const unselectQuestion = (uuid: string) => {
  selectedQuestionUuids.value.delete(uuid);
};

const clearAllSelections = () => {
  selectedQuestionUuids.value.clear();
};

const isChildQuestionSelected = (question: QuestionModel) => {
  const { uuid, parentQuestionUuid, topParentQuestionUuid } = question;

  return [uuid, parentQuestionUuid, topParentQuestionUuid].filter(Boolean).some(isQuestionSelected);
};

const handleUnselectAllChildrenQuestion = (question: QuestionModel) => {
  question.children.forEach((child) => {
    unselectQuestion(child.uuid);
    child.children.forEach((grandChild) => {
      unselectQuestion(grandChild.uuid);
    });
  });
};

const handleQuestionSelectionChange = (question: QuestionModel) => {
  if (isQuestionSelected(question.uuid)) {
    unselectQuestion(question.uuid);
  } else {
    clearAllSelections();
    selectQuestion(question.uuid);
  }
};

const handleChildrenQuestionSelectionChange = (
  question: QuestionModel,
  parentQuestion: QuestionModel,
) => {
  if (isQuestionSelected(question.uuid)) {
    unselectQuestion(question.uuid);
  } else if (question.parentQuestionUuid && isQuestionSelected(question.parentQuestionUuid)) {
    unselectQuestion(question.parentQuestionUuid);
    parentQuestion.children.forEach((child) => {
      if (child.uuid !== question.uuid) {
        selectQuestion(child.uuid);
      }
    });
  } else if (question.topParentQuestionUuid && isQuestionSelected(question.topParentQuestionUuid)) {
    unselectQuestion(question.topParentQuestionUuid);
    unselectQuestion(question.parentQuestionUuid!);
    mpqQuestion.value.children.forEach((child) => {
      if (child.uuid !== question.parentQuestionUuid) {
        selectQuestion(child.uuid);
      }
      child.children.forEach((grandChild) => {
        if (grandChild.uuid !== question.uuid) {
          selectQuestion(grandChild.uuid);
        }
      });
    });
  } else {
    selectQuestion(question.uuid);
    handleUnselectAllChildrenQuestion(question);
  }
};

const handleChangeQuestionPreview = (question: QuestionModel) => {
  emit("change-question-preview", question);
};

watch(
  () => Array.from(selectedQuestionUuids.value),
  (newValue) => {
    emit("select", newValue);
  },
);

onBeforeMount(() => {
  firstQuestionViewed.value = currentQuestion.value;
});
</script>

<style lang="scss">
@import "@styles/abstracts/mixins";
@import "@styles/layout/z-map";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.search-resource-detail-question-parts {
  .selection-bar {
    border-radius: 10px 10px 0 0;

    &._active {
      ~ .question-parts {
        border-radius: 0 0 10px 10px;
      }
    }
  }

  .child-questions {
    margin-top: $spacing-05;

    // Override default styling from Carbon Accordion
    > .bx--accordion {
      position: relative;

      > .input-checkbox,
      > .bx--btn {
        position: absolute;
        z-index: z-map("default");
      }

      > .bx--btn {
        top: 1rem;
        right: 2.5rem;
      }

      > .input-checkbox {
        top: 0.5rem;
        left: 1rem;

        // Styling when the checkbox is present
        ~ .bx--accordion__item {
          .bx--accordion__title {
            margin-left: $layout-04;
          }

          .bx--accordion__heading {
            .sls-icon {
              &[name*="CourseLocator"] {
                left: -5.5rem;
              }
            }
          }
        }
      }

      .bx--accordion__item {
        &.bx--accordion__item--active {
          .bx--accordion__heading {
            .bx--accordion__title {
              &::after {
                top: 19px;
                border-color: $interactive-01;
              }
            }
          }
        }

        .bx--accordion__heading {
          padding: $spacing-05 $spacing-03;
          background-color: $ui-01;
          color: $text-01;

          .input-checkbox {
            .sls-icon {
              [stroke] {
                stroke: $ui-04;
              }
            }
          }

          .sls-icon {
            &[name*="CourseLocator"] {
              left: -3.5rem;
            }
          }
        }

        .bx--accordion__title {
          display: flex;
          align-items: center;
          margin-left: $spacing-05;
          padding-right: $layout-05;
          // To ensure that the height is consistent with or without the checkbox
          // so that the icons can be aligned correctly
          height: 40px;

          &::after {
            top: 11px;
          }
        }
      }

      .bx--accordion__content {
        border-top: $divider-02;
        margin: 0 $spacing-05;
        padding: 0;

        .sls-icon {
          &[name*="CourseLocator"] {
            left: -2rem;
          }
        }
      }
    }

    .question-wrapper {
      padding: $spacing-05;
      background-color: $ui-01;
      border-radius: 10px;
    }

    > * {
      &:not(last-child) {
        margin-bottom: $spacing-05;
      }
    }
  }

  .question-parts {
    padding: $spacing-05;
    background-color: $ui-background;
    border-radius: 10px;

    .question-wrapper {
      display: flex;
      align-items: center;

      > .input-checkbox {
        margin: 0;
      }
    }

    .icon-button {
      margin-left: auto;
    }
  }

  .question-body {
    @include line-clamp(2);
    @include carbon--type-style("body-short-01");
  }

  .question-icon {
    min-width: 32px;
    margin-right: $spacing-03;
  }

  .question-label {
    @include carbon--type-style("helper-italic-01");
    margin-right: $spacing-02;
    color: $text-02;
    font-weight: 700;
  }

  .sls-icon {
    &[name*="CourseLocator"] {
      position: absolute;
      left: 0;
    }
  }
}
</style>
